const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  if (base.endsWith("/admin")) {
    return `${base}/task`;
  }
  return `${base}/admin/task`;
};

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("token");
  const headers = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

const handleResponse = async (response) => {
  // Read the body once as text, so a response that is not JSON can be reported
  // as such instead of being quietly turned into {}. That silent {} is how a
  // missing API route surfaced as "Cannot read properties of undefined" three
  // layers away, in a component that had no idea the request had failed.
  const raw = await response.text();
  let data = {};
  let isJson = true;

  if (raw.trim()) {
    try {
      data = JSON.parse(raw);
    } catch {
      isJson = false;
    }
  }

  if (!response.ok || data?.status === false) {
    const errorMsg = data?.message || data?.error || `Request failed with status ${response.status}`;
    const err = new Error(errorMsg);
    err.response = data;
    // 404 and 500 mean completely different things to whoever has to fix this
    // — a route that was never deployed, versus one that ran and broke — so
    // the status travels with the error rather than being flattened into prose.
    err.status = response.status;
    throw err;
  }

  if (!isJson) {
    // A 200 that is not JSON is almost always a proxy, or an SPA fallback,
    // answering for a route this deployment of the API does not have.
    const err = new Error(
      `The server returned a non-JSON response (HTTP ${response.status}). This API route may be missing from the deployed backend.`
    );
    err.response = raw.slice(0, 200);
    throw err;
  }

  return data;
};

// ==========================================
// Task Management (see px's src/admin/task module)
// ==========================================

export const fetchTaskList = async ({
  page = 1,
  limit = 20,
  search = "",
  taskType = "",
  platform = "",
  isActive = "",
  eventId = "",
  country = "",
} = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (search) query.append("search", search.trim());
  if (taskType) query.append("task_type", taskType);
  if (platform) query.append("platform", platform);
  if (isActive !== "") query.append("is_active", isActive);
  // `none` is a real filter — the tasks nobody filed under an event, which
  // are invisible in the app and need finding.
  if (eventId !== "") query.append("event_id", eventId);
  // `global`, or a code for the tasks targeted at that country.
  if (country) query.append("country", country);

  const res = await fetch(`${getApiBase()}/list?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** `payload` is a FormData when a new image is attached, a plain object otherwise. */
export const createTask = async (payload, isMultipart = false) => {
  const res = await fetch(`${getApiBase()}/create`, {
    method: "POST",
    headers: getAuthHeaders(isMultipart),
    body: isMultipart ? payload : JSON.stringify(payload),
  });
  return handleResponse(res);
};

export const fetchTaskDetails = async (id) => {
  const res = await fetch(`${getApiBase()}/view/${id}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const editTask = async (id, payload, isMultipart = false) => {
  const res = await fetch(`${getApiBase()}/edit/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(isMultipart),
    body: isMultipart ? payload : JSON.stringify(payload),
  });
  return handleResponse(res);
};

export const deleteTask = async (id) => {
  const res = await fetch(`${getApiBase()}/delete/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const setTaskActive = async (id, isActive) => {
  const res = await fetch(`${getApiBase()}/${isActive ? "activate" : "deactivate"}/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchTaskCompletions = async ({ page = 1, limit = 20, taskId = "", userId = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (taskId) query.append("task_id", taskId);
  if (userId) query.append("user_id", userId);

  const res = await fetch(`${getApiBase()}/completions?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

export const fetchTaskHistory = async (id, { page = 1, limit = 20 } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);

  const res = await fetch(`${getApiBase()}/history/${id}?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// Daily Task Engine (px's src/admin/task/schedule.service.js)
//
// `tasks` is the pool an admin stocks; `task_occurrences` is what the daily
// job publishes from it. The mobile app only ever sees today's live
// occurrences, so these are the calls that decide what users actually get.
// ==========================================

export const fetchTaskPool = async ({ page = 1, limit = 20, search = "", status = "", taskType = "", platform = "" } = {}) => {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (limit) query.append("limit", limit);
  if (search) query.append("search", search.trim());
  if (status) query.append("status", status);
  if (taskType) query.append("task_type", taskType);
  if (platform) query.append("platform", platform);

  const res = await fetch(`${getApiBase()}/pool?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** `from`/`to` are UTC `YYYY-MM-DD`. Omit both for today through the generation window. */
export const fetchTaskSchedule = async ({ from = "", to = "" } = {}) => {
  const query = new URLSearchParams();
  if (from) query.append("from", from);
  if (to) query.append("to", to);

  const res = await fetch(`${getApiBase()}/schedule?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** Force a task onto `day`, past every selection rule but the one-per-day key. */
export const pinTaskToDay = async (day, taskId) => {
  const res = await fetch(`${getApiBase()}/schedule/${day}/pin`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ task_id: taskId }),
  });
  return handleResponse(res);
};

/** Only works on a `scheduled` occurrence — a live one is pulled, never removed. */
export const removeOccurrence = async (occurrenceId) => {
  const res = await fetch(`${getApiBase()}/schedule/${occurrenceId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/** The mid-day kill switch. Points already earned are not clawed back. */
export const pullOccurrence = async (occurrenceId, reason = "") => {
  const res = await fetch(`${getApiBase()}/occurrence/${occurrenceId}/pull`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  return handleResponse(res);
};

export const fetchScheduleSettings = async () => {
  const res = await fetch(`${getApiBase()}/settings`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Move tasks into an event — no copy left behind.
 *
 * Pass `null` as the event to take tasks out of every event, which hides them
 * from the app without deleting them.
 */
export const moveTasksToEvent = async (taskIds, eventId) => {
  const res = await fetch(`${getApiBase()}/move-to-event`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ task_ids: taskIds, event_id: eventId }),
  });
  return handleResponse(res);
};

/** Partial — only the keys you pass are written. `null` means "uncapped". */
export const updateScheduleSettings = async (payload) => {
  const res = await fetch(`${getApiBase()}/settings`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
};

/** Run the generator now rather than waiting for the 00:05 cron. Idempotent. */
export const generateSchedule = async () => {
  const res = await fetch(`${getApiBase()}/schedule/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Every page of a website this server can find — sitemap first, the
 * homepage's own links as a fallback. Used by the page picker so an admin
 * chooses a page instead of pasting a URL.
 */
export const discoverSitePages = async (url) => {
  const query = new URLSearchParams({ url });
  const res = await fetch(`${getApiBase()}/discover-pages?${query.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// ==========================================
// Tasks belong to an event (px's src/admin/task/task.service.js)
// ==========================================

/** Events a task can be filed under, newest first, with how many each holds. */
export const fetchTaskEvents = async () => {
  const res = await fetch(`${getApiBase()}/events`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Copy tasks into another event.
 *
 * A copy, not a move — the originals stay put and each new row is independent,
 * which is what makes reusing a previous event's tasks safe.
 */
export const copyTasksToEvent = async (taskIds, eventId) => {
  const res = await fetch(`${getApiBase()}/copy-to-event`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ task_ids: taskIds, event_id: eventId }),
  });
  return handleResponse(res);
};
